/**
 * ProductImpressionTracker
 * Tracks product impressions using Intersection Observer API
 */
class ProductImpressionTracker {
  constructor(options = {}) {
    this.visibleProducts = new Map(); // Map to track visible products
    this.trackedProducts = new Set(); // Set to track products already sent to datalayer
    this.impressionTimeout = 2000; // 2 seconds timeout
    this.debug = options.debug || false;

    // Initialize the tracker
    this.init(options);
  }

  init(options) {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupTracker(options));
    } else {
      this.setupTracker(options);
    }
  }

  setupTracker(options) {
    if (this.debug) {
      console.log('%cProductImpressionTracker initialized', 'background: #4CAF50; color: white; padding: 2px 5px; border-radius: 3px;');
    }

    // Create the Intersection Observer
    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        root: null, // Use viewport as root
        rootMargin: '0px',
        threshold: 0.5 // 50% visibility threshold
      }
    );

    // Start observing product cards
    this.observeProductCards();
  }

  observeProductCards() {
    // Find all product cards on the page
    const productCards = document.querySelectorAll('[data-product-id]');

    if (this.debug) {
      console.log(`%cFound ${productCards.length} product cards to observe`, 'background: #2196F3; color: white; padding: 2px 5px; border-radius: 3px;');
    }

    // Observe each product card
    productCards.forEach(card => {
      const productId = card.getAttribute('data-product-id');

      if (this.debug) {
        console.log(`%cStarted observing product card: ${productId}`, 'background: #FF9800; color: white; padding: 2px 5px; border-radius: 3px;');
      }

      this.observer.observe(card);
    });
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      const productCard = entry.target;
      const productId = productCard.getAttribute('data-product-id');

      if (entry.isIntersecting) {
        // Product is visible
        if (this.debug) {
          console.log(`%cProduct card ${productId} is now visible at ${new Date().toLocaleTimeString()}`, 'background: #9C27B0; color: white; padding: 2px 5px; border-radius: 3px;');
        }

        // Start tracking impression after timeout
        setTimeout(() => {
          if (this.debug) {
            console.log(`%cTracking impression for product ${productId} after 2-second delay at ${new Date().toLocaleTimeString()}`, 'background: #FF5722; color: white; padding: 2px 5px; border-radius: 3px;');
          }
          this.trackImpression(productCard);
        }, this.impressionTimeout);
      } else {
        // Product is no longer visible
        this.visibleProducts.delete(productId);
      }
    });
  }

  trackImpression(productCard) {
    const productId = productCard.getAttribute('data-product-id');

    // Skip if we've already tracked this product in this session
    if (this.trackedProducts.has(productId)) {
      if (this.debug) {
        console.log(`%cSkipping duplicate impression for product ${productId}`, 'background: #FFC107; color: black; padding: 2px 5px; border-radius: 3px;');
      }
      return;
    }

    // Skip if we've already tracked this product
    if (this.visibleProducts.has(productId)) {
      return;
    }

    // Mark this product as tracked
    this.visibleProducts.set(productId, true);
    this.trackedProducts.add(productId);

    // Collect product data
    const impressionData = {
      productId: productId,
      variantId: productCard.getAttribute('data-variant-id'),
      title: productCard.getAttribute('data-product-title'),
      vendor: productCard.getAttribute('data-product-vendor'),
      price: productCard.getAttribute('data-product-price'),
      currency: productCard.getAttribute('data-product-currency'),
      url: productCard.getAttribute('data-product-url'),
      image: productCard.getAttribute('data-product-image'),
      category: productCard.getAttribute('data-product-category'),
      variant: productCard.getAttribute('data-product-variant'),
      listName: this.getProductListName(productCard),
      position: this.getProductPosition(productCard)
    };

    if (this.debug) {
      console.log('%cTracked impression:', 'background: #E91E63; color: white; padding: 2px 5px; border-radius: 3px;', impressionData);
    }

    // Send data to analytics
    this.sendImpressionData(impressionData);
  }

  sendImpressionData(data) {
    // Send data to your analytics endpoint
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'product_impression',
        'ecommerce': {
          'impressions': [{
            'id': data.productId,
            'name': data.title,
            'price': data.price,
            'brand': data.vendor,
            'category': data.category,
            'variant': data.variant,
            'list': data.listName,
            'position': data.position
          }]
        }
      });

      if (this.debug) {
        console.log('%cSent impression data to dataLayer', 'background: #009688; color: white; padding: 2px 5px; border-radius: 3px;');
      }
    }
  }

  getProductListName(productCard) {
    // Try to determine the product list name based on context
    const productList = document.querySelector('.product-list');

    if (productList) {
      // Check for specific product list types
      if (productList.classList.contains('recently-viewed-products')) {
        return 'Recently Viewed Products';
      } else if (productList.classList.contains('shop-the-look')) {
        return 'Shop The Look';
      } else if (productList.classList.contains('related-products')) {
        return 'Related Products';
      } else if (productList.classList.contains('featured-collection')) {
        return 'Featured Collection';
      } else {
        // Default to page type if no specific list is determined
        return document.body.dataset.pageType || 'Product List';
      }
    }

    return 'unknown';
  }

  getProductPosition(productCard) {
    // Get the product ID from the card
    const productId = productCard.getAttribute('data-product-id');

    // Find all product cards
    const productCards = document.querySelectorAll('[data-product-id]');

    // Find the position of this product
    for (let i = 0; i < productCards.length; i++) {
      if (productCards[i].getAttribute('data-product-id') === productId) {
        return i + 1; // 1-based position
      }
    }

    return 0; // Default position if not found
  }
}

export { ProductImpressionTracker };

// Initialize the tracker when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', () => {
  window.productImpressionTracker = new ProductImpressionTracker();
});
